import { useState } from 'react';
import { Icon } from '../icons';
import { api } from '../../lib/api';
import {
  PACKAGE_MOVIE_COUNT,
  PACKAGE_ORDER,
  PACKAGE_PRICES,
  appendGrowCheckoutCorrelationParams,
  movieCountHebrew,
  resolveClientGrowCheckoutUrl,
} from '../../lib/pricing';
import type { PackageId } from '../../lib/types';

interface Props {
  jobId: string | null;
  value: PackageId;
  onChange: (p: PackageId) => void;
  onCancel: () => void;
  onBack: () => void;
}

interface Pkg {
  id: PackageId;
  name: string;
  price: number;
  features: string[];
  recommended?: boolean;
}

const PKGS: Pkg[] = PACKAGE_ORDER.map((id) => {
  const n = PACKAGE_MOVIE_COUNT[id];
  return {
    id,
    name: movieCountHebrew(n),
    price: PACKAGE_PRICES[id],
    recommended: id === 'plus',
    features: [
      `רכישת זכות ל־${n} ${n === 1 ? 'סרטון' : 'סרטונים'}`,
      'כל החבילות כוללות את אותו תהליך ואותה איכות — משתנה רק מספר הסרטונים.',
    ],
  };
});

export function PricingScreen({ jobId, value, onChange, onCancel, onBack }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  async function purchase() {
    if (!jobId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.meshulamCreateCheckout({
        jobId,
        pkg: value,
        buyer: { fullName: fullName.trim() || 'לקוח/ה', phone, email },
      });
      const fakeLocal = res.url.includes('fake=1');
      const growUrl = resolveClientGrowCheckoutUrl(value).trim();
      const growWithCorrelation =
        growUrl && jobId
          ? appendGrowCheckoutCorrelationParams(growUrl, jobId, res.processId)
          : growUrl;
      const target = fakeLocal ? res.url : growWithCorrelation || res.url;
      const win = window.top ?? window;
      win.location.assign(target);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה ביצירת תשלום');
      setBusy(false);
    }
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="icon-btn" onClick={onBack}>
          <Icon.ChevronRight size={20} />
        </button>
        <div className="step-counter">שלב 5 מתוך 5</div>
        <div style={{ width: 38 }} />
      </div>
      <div className="screen-scroll">
        <div className="eyebrow" style={{ marginTop: 24 }}>
          תשלום חד־פעמי
        </div>
        <h1 className="h1">בוחרים חבילה</h1>
        <p className="lede">החבילות נבדלות רק במספר הסרטונים ברכישה — 1, 3 או 10.</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {PKGS.map((p) => (
            <div
              key={p.id}
              className={
                'pkg-card' +
                (value === p.id ? ' is-selected' : '') +
                (p.recommended ? ' is-recommended' : '')
              }
              onClick={() => onChange(p.id)}
            >
              {p.recommended && <div className="ribbon">הכי פופולרי</div>}
              <div className="pkg-head">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      border: value === p.id ? '6px solid var(--accent)' : '2px solid var(--line)',
                      background: value === p.id ? 'white' : 'transparent',
                      flexShrink: 0,
                      transition: 'all .15s',
                    }}
                  />
                  <div className="pkg-name">{p.name}</div>
                </div>
                <div className="pkg-price">
                  <span className="num">{p.price}</span>
                  <span className="cur">₪</span>
                </div>
              </div>
              <ul>
                {p.features.map((f) => (
                  <li key={f}>
                    <Icon.Check size={13} /> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <BuyerField label="שם מלא" value={fullName} onChange={setFullName} placeholder="ישראל ישראלי" />
          <BuyerField
            label="אימייל"
            value={email}
            onChange={setEmail}
            placeholder="you@example.com"
            type="email"
          />
          <BuyerField label="טלפון" value={phone} onChange={setPhone} placeholder="050-1234567" type="tel" />
        </div>

        {error && (
          <div
            className="tip-row"
            style={{ background: 'oklch(0.92 0.05 30 / 0.6)', color: 'var(--ink)' }}
          >
            <div>{error}</div>
          </div>
        )}

        <div className="tip-row" style={{ background: 'oklch(0.95 0.02 60)' }}>
          <div className="tip-glyph" style={{ color: 'var(--ink-soft)' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M3 10h18" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div>תשלום מאובטח דרך Grow / Meshulam · ביטול והחזר תוך 7 ימים אם לא אהבתם</div>
        </div>

        <button
          onClick={onCancel}
          style={{
            marginTop: 8,
            background: 'transparent',
            border: 'none',
            color: 'var(--ink-mute)',
            fontFamily: 'Heebo',
            fontWeight: 500,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          ביטול והחזרה לתצוגה המקדימה
        </button>
      </div>
      <div className="cta-dock">
        <button className="btn-primary" disabled={busy || !jobId} onClick={purchase}>
          {busy ? 'מעבירים לתשלום...' : `תשלום · ${PKGS.find((p) => p.id === value)?.price ?? 0} ₪`}
        </button>
      </div>
    </div>
  );
}

function BuyerField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (s: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontFamily: 'Heebo',
        fontSize: 13,
        color: 'var(--ink-soft)',
      }}
    >
      {label}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          padding: '12px 14px',
          borderRadius: 12,
          border: '1px solid var(--line)',
          background: 'var(--bg-elevated)',
          fontSize: 15,
          fontFamily: 'Assistant, system-ui, sans-serif',
          color: 'var(--ink)',
          direction: 'rtl',
        }}
      />
    </label>
  );
}
